<template>
  <div
    v-if="relatedResourcesLinks?.length"
    ref="relatedResources"
    :class="allClasses"
  >
    <component
      :is="type"
      :key="relatedResourcesLinks.length"
      :heading="relatedResourcesHeading"
      :links="relatedResourcesLinks"
      :modifiers="modifiers"
      :with-icons="'after'"
    />
  </div>
</template>

<script>
import {
  ListOfLinksWithHeading,
  ListOfLinks,
  useEventBus
} from "@thegetty-private/getty-ui";
import { getIdManagerDataUrl } from "@/helpers/configHelper";
import { getRelatedResourcesLinks } from "@/stores/localActionSupport";

export default {
  name: "RelatedResources",
  components: { ListOfLinksWithHeading, ListOfLinks },
  provide() {
    return {
      analyticsListener: (_event, url, _to) => {
        this.logEvent(url);
      }
    };
  },
  props: {
    /**
     * Exclude External Hosts
     */
    excludeExternalHosts: {
      type: Array,
      default: () => {
        return [];
      }
    },
    /**
     * Array of objects used to construct GuiLinks
     * @example [ { text: {string}, to: {object} } ]
     */
    links: {
      type: Array,
      default: () => {
        return [];
      }
    },
    /**
     * The Object id for the item being looked up
     */
    id: {
      type: String,
      default: undefined,
      required: true
    },
    /**
     * An array of classes to apply on the top-level div
     */
    classes: {
      type: Array,
      default: () => {
        return [];
      }
    },
    /**
     * An array of modifiers for Getty UI List of Links with Heading component
     */
    modifiers: {
      type: Array,
      default: () => {
        return [];
      }
    },
    /**
     * Whether to include heading
     */
    hasHeading: {
      type: Boolean,
      default: true
    }
  },
  emits: ["hasContent"],
  data() {
    return {
      /**
       * Event bus composable instance
       */
      eventBus: useEventBus(),
      /**
       * Related Content
       * @type {array}
       */
      relatedResourcesLinks: []
    };
  },
  computed: {
    /**
     * all the css classes for the item
     *
     * @returns {array}
     */
    allClasses() {
      return ["m-related-resources"].concat(this.classes);
    },
    /**
     * The heading for the related resources section
     *
     * @returns {string}
     */
    relatedResourcesHeading() {
      return this.$t("exhibition_page.related_resources.label");
    },
    /**
     * The type of component to render
     *
     * @returns {string}
     *
     */
    type() {
      if (this.hasHeading) {
        return "ListOfLinksWithHeading";
      }
      return "ListOfLinks";
    }
  },
  watch: {
    links: "fetchData"
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    /**
     * get the related resources from the ID manager
     */
    async fetchData() {
      this.relatedResourcesLinks = await getRelatedResourcesLinks(
        this.id,
        this.links,
        this.excludeExternalHosts,
        getIdManagerDataUrl()
      );
      if (this.relatedResourcesLinks.length > 0) {
        this.$emit("hasContent", true);
      }
    },
    /**
     * Log the click event
     *
     * @param {object} to
     */
    logEvent(url) {
      if (url) {
        this.eventBus.emit("AnalyticsEvent", {
          action: "related resource",
          category: "Related Resource",
          label: url,
          noninteraction: true
        });
      }
    }
  }
};
</script>

<style lang="scss">
@import "./index.scss";
</style>
